import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation} from 'react-router';

import noop from 'lodash/noop';

import {Button, NewTextareaField, showModal} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {InlineLink, SecondaryText, Text} from 'src/components/primitives';
import {useSendUserFeedbackMutation} from 'src/containers/User/api';
import {useAuthorizedMetrics, useEdnaLinks} from 'src/hooks';
import {AdvertisingIcon} from 'src/icons';
import {joiResolver} from 'src/utils';

import {MODALS_CONTAINER_ID} from '../definitions';
import * as SS from '../style';
import {showSuccessModal} from './SuccessModal';
import * as S from './style';

const FeedbackWidget = React.memo<{onClose: TAnyFunction}>(({onClose}) => {
  const {t} = useTranslation();

  const location = useLocation();
  const links = useEdnaLinks();
  const [sendFeedback] = useSendUserFeedbackMutation();
  const {sendMetrics, attachMetrics} = useAuthorizedMetrics();

  const methods = useForm({
    defaultValues: {
      text: '',
    },
    resolver: joiResolver((joi) =>
      joi
        .object({
          text: joi.string().required(),
        })
        .required(),
    ),
  });

  React.useEffect(() => {
    sendMetrics('feedback-form-opened');
  }, []);

  const handleSubmit = React.useCallback(
    (values: {text: string}) => {
      sendFeedback({
        feedback: values.text,
        feedbackPage: location.pathname,
      })
        .unwrap()
        .then(() => {
          onClose();
          showSuccessModal();
        })
        .catch(() => {});
    },
    [location.pathname],
  );

  return (
    <FormProvider {...methods}>
      <S.Container>
        <S.Header>
          <Text weight={500}>{t('HelpWidget:feedbackForm.title')}</Text>
          <S.CloseIcon onClick={attachMetrics('feedback-form-canceled', onClose)} />
        </S.Header>
        <S.Content>
          <Stack size="2" justify="stretch">
            <SecondaryText size="S">{t('HelpWidget:feedbackForm.description')}</SecondaryText>
            <NewTextareaField name="text" rows={11} noMargin />
          </Stack>
          <Stack align="space-between" style={{flexGrow: 1}}>
            <SecondaryText size="XS">
              <Trans i18nKey="HelpWidget:feedbackForm.hint" values={{link: links.SUPPORT_EMAIL}}>
                <InlineLink href={`mailto:${links.SUPPORT_EMAIL}`} />
              </Trans>
            </SecondaryText>
            <Stack direction="ROW" size="2" style={{marginLeft: 'auto'}}>
              <Button
                appearance="secondary"
                onClick={attachMetrics('feedback-form-canceled', onClose)}
              >
                {t('Common:button.cancel2')}
              </Button>
              <Button appearance="primary" onClick={methods.handleSubmit(handleSubmit)}>
                {t('Common:button.submit')}
              </Button>
            </Stack>
          </Stack>
        </S.Content>
      </S.Container>
    </FormProvider>
  );
});

const showFeedbackWidget = (onClose: TAnyFunction) =>
  showModal(
    (closeModal) => (
      <FeedbackWidget
        onClose={() => {
          closeModal();
          onClose();
        }}
      />
    ),
    {},
    MODALS_CONTAINER_ID,
  );

const FeedbackWidgetButton = React.memo(() => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <SS.WidgetButton
      isOpen={isOpen}
      isFixed
      onClick={
        isOpen
          ? noop
          : () => {
              showFeedbackWidget(() => setIsOpen(false));
              setIsOpen(true);
            }
      }
    >
      <Icon size="32px" as={AdvertisingIcon} />
    </SS.WidgetButton>
  );
});

export {showFeedbackWidget, FeedbackWidgetButton};
